import { createContext, useCallback, useContext, useRef, useState } from "react";
import { deepCopy, normalizeDashboardQueryState } from "../visualQueryStateUtils";
import {
    VISUAL_MODEL_KIND,
    getDefaultVisualExecutionMode,
    normalizeVisualExecutionMode,
} from "../visualExecutionModes";

export const VisualFormContext = createContext();

const createComboKey = (visual) => {
    const modelKind = Number(visual?.ModelKind ?? 0);
    const modelId = String(visual?.ModelId ?? "").trim();
    const visualTypeId = String(visual?.VisualTypeId ?? "").trim();

    if (!modelKind || !modelId || !visualTypeId) {
        return null;
    }

    return `${modelKind}::${modelId}::${visualTypeId}`;
};

const extractComboDraft = (visual) => ({
    CustomFields: deepCopy(visual?.CustomFields ?? {}),
    VisualColumns: deepCopy(visual?.VisualColumns ?? []),
    RenderSettings: deepCopy(visual?.RenderSettings ?? {}),
    ExecutionMode: normalizeVisualExecutionMode(
        visual?.ModelKind,
        visual?.ExecutionMode,
        visual?.DefaultQueryState?.SupportsRealTime ?? true
    ),
    DefaultQueryState: normalizeDashboardQueryState(visual?.DefaultQueryState, {
        ModelKind: Number(visual?.ModelKind ?? 0),
        ModelId: visual?.ModelId ?? "",
    }),
});

const createCleanComboState = (visual, sourceVisual = null) => ({
    ...visual,
    ExecutionMode: normalizeVisualExecutionMode(
        visual?.ModelKind,
        visual?.ExecutionMode,
        visual?.DefaultQueryState?.SupportsRealTime ?? true
    ),
    CustomFields: deepCopy(sourceVisual?.CustomFields ?? visual?.CustomFields ?? {}),
    VisualColumns: deepCopy(sourceVisual?.VisualColumns ?? visual?.VisualColumns ?? []),
    RenderSettings: {},
        DefaultQueryState: normalizeDashboardQueryState(visual?.DefaultQueryState, {
            ModelKind: Number(visual?.ModelKind ?? 0),
            ModelId: visual?.ModelId ?? "",
            SupportsRealTime: true,
            SelectColumns: [],
            VisibleColumns: [],
            GroupByColumns: [],
            MeasureNames: [],
            VisualAggregates: [],
            Sorts: [],
            Groupings: [],
            FilterDraft: [],
        }),
});

const applyComboDraft = (visual, comboDraft) => ({
    ...visual,
    ExecutionMode: normalizeVisualExecutionMode(
        visual?.ModelKind,
        comboDraft?.ExecutionMode ?? visual?.ExecutionMode,
        comboDraft?.DefaultQueryState?.SupportsRealTime ?? visual?.DefaultQueryState?.SupportsRealTime ?? true
    ),
    CustomFields: deepCopy(comboDraft?.CustomFields ?? {}),
    VisualColumns: deepCopy(comboDraft?.VisualColumns ?? []),
    RenderSettings: deepCopy(comboDraft?.RenderSettings ?? {}),
    DefaultQueryState: normalizeDashboardQueryState(comboDraft?.DefaultQueryState, {
        ModelKind: Number(visual?.ModelKind ?? 0),
        ModelId: visual?.ModelId ?? "",
    }),
});

export const VisualFormContextProvider = ({ children }) => {
    const [visualObject, setVisualObject] = useState();
    const comboDraftCacheRef = useRef(new Map());

    const clearVisualComboDraftCache = useCallback(() => {
        comboDraftCacheRef.current = new Map();
    }, []);

    const switchVisualCombo = useCallback((updater) => {
        setVisualObject((prev) => {
            const currentVisual = prev ?? {};
            const currentComboKey = createComboKey(currentVisual);
            if (currentComboKey) {
                comboDraftCacheRef.current.set(currentComboKey, extractComboDraft(currentVisual));
            }

            const nextVisual =
                typeof updater === "function"
                    ? updater(currentVisual)
                    : { ...currentVisual, ...(updater ?? {}) };

            const cleanedVisual = createCleanComboState(nextVisual, currentVisual);
            const nextComboKey = createComboKey(cleanedVisual);

            if (nextComboKey && comboDraftCacheRef.current.has(nextComboKey)) {
                return applyComboDraft(cleanedVisual, comboDraftCacheRef.current.get(nextComboKey));
            }

            return cleanedVisual;
        });
    }, []);

    const setVisualModelKind = useCallback((modelKind) => {
        const nextKind = Number(modelKind) || 0;
        switchVisualCombo((prev) => ({
            ...prev,
            ModelKind: nextKind,
            ModelId: "",
            ExecutionMode: getDefaultVisualExecutionMode(nextKind, true),
        }));
    }, [switchVisualCombo]);

    const setVisualModelId = useCallback((modelId) => {
        switchVisualCombo((prev) => ({
            ...prev,
            ModelId: modelId,
        }));
    }, [switchVisualCombo]);

    const setVisualTypeId = useCallback((visualTypeId) => {
        switchVisualCombo((prev) => ({
            ...prev,
            VisualTypeId: visualTypeId,
        }));
    }, [switchVisualCombo]);

    const setVisualExecutionMode = useCallback((executionMode) => {
        setVisualObject((prev) => {
            if (!prev) {
                return prev;
            }

            return {
                ...prev,
                ExecutionMode: normalizeVisualExecutionMode(
                    prev?.ModelKind,
                    executionMode,
                    prev?.DefaultQueryState?.SupportsRealTime ?? true
                ),
            };
        });
    }, []);

    const closeVisualCreationDialog = useCallback(() => {
        clearVisualComboDraftCache();
        setVisualObject()
    }, [clearVisualComboDraftCache]);

    const openVisualCreation = useCallback((coordinateItem) => {
        clearVisualComboDraftCache();
        setVisualObject({
            ModelKind: VISUAL_MODEL_KIND.TABLE,
            ModelId: "",
            VisualTypeId: "",
            ExecutionMode: getDefaultVisualExecutionMode(VISUAL_MODEL_KIND.TABLE, true),
            PositionX: coordinateItem.x,
            PositionY: coordinateItem.y,
            Width: coordinateItem.w,
            Height: coordinateItem.h,
            DefaultQueryState: normalizeDashboardQueryState(),
        });
    }, [clearVisualComboDraftCache]);

    const openVisualEdit = useCallback((visual) => {
        const {
            VisualColorPaletteOptionId,
            VisualRenderOptionId,
            PaletteName,
            ...rest
        } = visual ?? {};

        clearVisualComboDraftCache();
        setVisualObject({
            ...rest,
            ExecutionMode: normalizeVisualExecutionMode(
                rest?.ModelKind,
                rest?.ExecutionMode,
                rest?.DefaultQueryState?.SupportsRealTime ?? true
            ),
            DefaultQueryState: normalizeDashboardQueryState(rest?.DefaultQueryState, {
                ModelKind: rest?.ModelKind ?? 0,
                ModelId: rest?.ModelId ?? "",
            }),
        });
    }, [clearVisualComboDraftCache]);

    const openVisualClone = useCallback((visual) => {
        const {
            VisualColorPaletteOptionId,
            VisualRenderOptionId,
            PaletteName,
            VisualId,
            UniqueId,
            _id,
            ...rest
        } = visual ?? {};

        clearVisualComboDraftCache();
        setVisualObject({
            ...rest,
            VisualId: undefined,
            UniqueId: undefined,
            _id: undefined,
            IsActive: true,
            ExecutionMode: normalizeVisualExecutionMode(
                rest?.ModelKind,
                rest?.ExecutionMode,
                rest?.DefaultQueryState?.SupportsRealTime ?? true
            ),
            DefaultQueryState: normalizeDashboardQueryState(rest?.DefaultQueryState, {
                ModelKind: rest?.ModelKind ?? 0,
                ModelId: rest?.ModelId ?? "",
            }),
        });
    }, [clearVisualComboDraftCache]);

    return (
        <VisualFormContext.Provider
            value={{
                visualObject,
                openVisualCreation,
                openVisualEdit,
                openVisualClone,
                setVisualObject,
                switchVisualCombo,
                setVisualModelKind,
                setVisualModelId,
                setVisualTypeId,
                setVisualExecutionMode,
                clearVisualComboDraftCache,
                closeVisualCreationDialog,
            }}
        >
            {children}
        </VisualFormContext.Provider>
    );
}

export const useVisualFormContext = () => useContext(VisualFormContext);
